import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtHM, fmtISO, fmtLong, snapTo15 } from '../lib/format.js';
import { HoursInput } from './HoursInput.jsx';
import { Icon } from './Icon.jsx';
import {
  ImportSplitAction,
  RowStatusChip,
  rowImportStatus,
} from './ImportRowStatus.jsx';
import { MinutesSegment } from './MinutesSegment.jsx';
import { Select } from './Select.jsx';
import { ClientSelect } from './ClientSelect.jsx';

/**
 * v2.1 — build the `createEntry` payload for a Jira-import row. Pure for
 * testability; encodes the new rule that the ticket and its parent epic
 * (when present) are stored side-by-side instead of conflated. Activity
 * field rules (`product: hidden` etc.) still strip per-field outputs.
 */
export function buildJiraImportEntry({ ticket, draft, fieldRules, isoDate }) {
  const productOut = fieldRules?.product !== 'hidden' ? draft.product || '' : '';
  const clientOut = fieldRules?.client !== 'hidden' ? draft.client || '' : '';
  return {
    date: isoDate,
    activityName: draft.activity,
    product: productOut,
    clientName: clientOut,
    ticketId: ticket.ticketId,
    ticketTitle: ticket.ticketTitle,
    epicId: ticket.parentEpicId || '',
    epicTitle: ticket.parentEpicTitle || '',
    hours: parseInt(draft.hours, 10) || 0,
    minutes: snapTo15(draft.minutes),
    description: (draft.description || '').trim(),
    jiraTicketKey: ticket.ticketId,
  };
}

/**
 * Drives the per-row create calls for a Jira import. Kept pure (takes
 * `createEntry` as a dependency) so the batch behaviour is unit-testable
 * without rendering the dialog.
 *
 * One rejected row must not abort the rest — the old code `break`-ed on the
 * first failure, which is why a single epic-less ticket made "half" the
 * import silently vanish. A 401 is the exception: the session is gone, so
 * we stop and let the app's global handler redirect to login.
 *
 * `rows` is `{ ticketId, payload }[]`. Returns
 * `{ created, createdIds: ticketId[], failed: ticketId[], busy, aborted }`
 * so the caller can surface one summary message *and* retire the rows that
 * already saved — otherwise a retry would re-submit them as duplicates.
 */
export async function runJiraImport({ rows, createEntry }) {
  let busy = false;
  const createdIds = [];
  const failed = [];
  for (const row of rows) {
    try {
      await createEntry(row.payload);
      createdIds.push(row.ticketId);
    } catch (err) {
      if (err?.code === 'UNAUTH')
        return { created: createdIds.length, createdIds, failed, busy, aborted: true };
      if (err?.status === 503) busy = true;
      failed.push(row.ticketId);
    }
  }
  return { created: createdIds.length, createdIds, failed, busy, aborted: false };
}

/**
 * Partition import rows into "ready" (importable now) vs "incomplete"
 * (selected but missing a required field). This is the single source of truth
 * for both the importer's own footer counts AND the v2.4 session saved-work
 * receipt — the expiry snapshot must never disagree with what the footer
 * showed, so both read from here rather than forking the predicate.
 */
export function classifyJiraRows({
  tickets,
  drafts,
  department,
  activityMeta = {},
  defaults,
}) {
  const fieldDefaults =
    defaults || { product: 'required', client: 'hidden', epic: 'hidden' };
  const importable = (tickets || []).filter((t) => {
    if (t.alreadyImported) return false;
    const d = drafts?.[t.ticketId];
    if (!d || !d.included || !d.activity) return false;
    const rules =
      activityMeta[`${department}::${d.activity}`]?.fieldRules || fieldDefaults;
    if (rules.product === 'required' && !d.product) return false;
    if (rules.client === 'required' && !d.client) return false;
    const mins =
      (parseInt(d.hours, 10) || 0) * 60 + (parseInt(d.minutes, 10) || 0);
    if (!mins) return false;
    return true;
  });
  const incomplete = (tickets || []).filter((t) => {
    if (t.alreadyImported) return false;
    const d = drafts?.[t.ticketId];
    return d && d.included && !importable.includes(t);
  });
  return { importable, incomplete };
}

function TicketChip({ ticketId, jiraBaseUrl }) {
  const href = jiraBaseUrl
    ? `${jiraBaseUrl.replace(/\/+$/, '')}/browse/${ticketId}`
    : null;
  const inner = (
    <>
      <Icon name="jiraMark" size={12} />
      {ticketId}
      {href && <Icon name="ext" size={10} stroke={2} />}
    </>
  );
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px 4px 6px',
    borderRadius: 6,
    background: '#E9F2FF',
    color: '#0052CC',
    fontFamily: 'DM Sans',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textDecoration: 'none',
    flex: 'none',
  };
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in Jira"
        style={baseStyle}
      >
        {inner}
      </a>
    );
  }
  return <span style={baseStyle}>{inner}</span>;
}

function EpicRef({ ticket }) {
  if (ticket.parentEpicId) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: 13,
          color: 'var(--sp-fg-3)',
          fontWeight: 500,
          maxWidth: 360,
          minWidth: 0,
          flex: 'none',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        <span style={{ color: 'var(--sp-fg-4)', margin: '0 8px' }}>·</span>
        <span style={{ flex: 'none' }}>↳ Epic </span>
        <span
          style={{
            fontFamily: 'DM Sans',
            color: '#0052CC',
            fontWeight: 700,
            flex: 'none',
          }}
        >
          {ticket.parentEpicId}
        </span>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          &nbsp;· {ticket.parentEpicTitle}
        </span>
      </span>
    );
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 13,
        color: 'var(--sp-fg-3)',
        fontStyle: 'italic',
        flex: 'none',
      }}
    >
      <span
        style={{ color: 'var(--sp-fg-4)', margin: '0 8px', fontStyle: 'normal' }}
      >
        ·
      </span>
      ↳ Standalone ticket
    </span>
  );
}

function TicketRow({ ticket, draft, dept, dictionary, onUpdate, jiraBaseUrl }) {
  const activityOptions = dictionary?.activitiesByDept?.[dept] || [];
  const activityMeta = dictionary?.activityMeta || {};
  const defaults = dictionary?.defaultFieldRules || {
    product: 'required',
    client: 'hidden',
    epic: 'hidden',
  };
  const descFor = (name) =>
    activityMeta[`${dept}::${name}`]?.description || '';
  const fieldRules = draft.activity
    ? activityMeta[`${dept}::${draft.activity}`]?.fieldRules || defaults
    : null;
  const hasActivity = !!draft.activity;
  // Match the calendar import — all three field slots are always rendered
  // so the row keeps its shape; they go disabled when the activity says
  // they don't apply. Epic is *always* locked in the Jira flow because
  // it's auto-derived from the ticket's parent (or the ticket itself).
  const productEnabled = hasActivity && fieldRules.product !== 'hidden';
  const clientEnabled = hasActivity && fieldRules.client !== 'hidden';
  const epicEnabled = hasActivity && fieldRules.epic !== 'hidden';
  const products = dictionary?.products || ['SwiftPay', 'SwiftGuard', 'None'];
  const clients = dictionary?.clients || [];

  if (ticket.alreadyImported) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '14px 16px',
          borderRadius: 12,
          background: 'var(--sp-bg-page)',
          opacity: 0.7,
          border: '1px dashed var(--sp-border)',
        }}
      >
        <TicketChip ticketId={ticket.ticketId} jiraBaseUrl={jiraBaseUrl} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--sp-fg-2)',
              textDecoration: 'line-through',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ticket.ticketTitle}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--sp-fg-3)',
              marginTop: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="check" size={12} stroke={2.4} /> Already logged ·
            skipped
          </div>
        </div>
      </div>
    );
  }

  const included = draft.included;
  const mins =
    (parseInt(draft.hours, 10) || 0) * 60 +
    (parseInt(draft.minutes, 10) || 0);
  const status = rowImportStatus(draft, {
    alreadyLogged: false,
    requiresProduct: hasActivity
      ? fieldRules.product === 'required'
      : defaults.product === 'required',
  });

  return (
    <div
      data-ticket-id={ticket.ticketId}
      style={{
        border: '1px solid var(--sp-border)',
        borderRadius: 12,
        background: included ? '#fff' : 'var(--sp-bg-page)',
        opacity: included ? 1 : 0.6,
        transition: 'opacity .15s, background .15s',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '14px 16px 12px',
        }}
      >
        <input
          type="checkbox"
          checked={included}
          onChange={(e) => onUpdate({ included: e.target.checked })}
          title={included ? 'Skip this ticket' : 'Include this ticket'}
          style={{
            width: 16,
            height: 16,
            accentColor: '#0052CC',
            cursor: 'pointer',
            flex: 'none',
          }}
        />
        <TicketChip ticketId={ticket.ticketId} jiraBaseUrl={jiraBaseUrl} />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0,
          }}
        >
          <input
            type="text"
            value={draft.description}
            onChange={(e) =>
              onUpdate({ description: e.target.value.slice(0, 200) })
            }
            placeholder="Description"
            disabled={!included}
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              border: 0,
              outline: 'none',
              background: 'transparent',
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--sp-fg-1)',
              padding: 0,
            }}
          />
          <EpicRef ticket={ticket} />
        </div>
        {included && (
          <span style={{ flex: 'none' }}>
            <RowStatusChip status={status} duration={fmtHM(mins)} />
          </span>
        )}
      </div>

      {included && (
        <div
          style={{
            padding: '0 16px 16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 10,
          }}
        >
          {/* Row 1: Time + Activity */}
          <div style={{ gridColumn: 'span 4' }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Time
            </label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
              <HoursInput
                value={draft.hours}
                onChange={(v) => onUpdate({ hours: v })}
                size="sm"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <MinutesSegment
                  value={draft.minutes}
                  onChange={(v) => onUpdate({ minutes: v })}
                  size="sm"
                />
              </div>
            </div>
          </div>

          <div style={{ gridColumn: 'span 8' }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Activity <span className="tt-field-required">*</span>
            </label>
            <Select
              value={draft.activity}
              options={activityOptions.map((a) => ({
                key: a,
                label: a,
                desc: descFor(a),
              }))}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              getDescription={(o) => o.desc}
              placeholder="Pick activity"
              onChange={(v) => onUpdate({ activity: v })}
              searchable
              searchPlaceholder="Search activities…"
              searchNoun="activity"
            />
          </div>

          {/* Row 2: Product · Client · Epic (locked) */}
          <div
            style={{ gridColumn: 'span 4', opacity: productEnabled ? 1 : 0.5 }}
          >
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Product
              {productEnabled && fieldRules.product === 'required' && (
                <span className="tt-field-required">*</span>
              )}
            </label>
            <Select
              value={productEnabled ? draft.product : ''}
              options={products.map((p) => ({ key: p, label: p }))}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              placeholder={hasActivity ? 'Not applicable' : 'Pick activity first'}
              onChange={(v) => onUpdate({ product: v })}
              disabled={!productEnabled}
            />
          </div>

          <div
            style={{ gridColumn: 'span 4', opacity: clientEnabled ? 1 : 0.5 }}
          >
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Client
              {clientEnabled && fieldRules.client === 'required' && (
                <span className="tt-field-required">*</span>
              )}
            </label>
            <ClientSelect
              value={clientEnabled ? draft.client : null}
              clients={clients}
              placeholder="Pick a client"
              emptyLabel="No client"
              onChange={(v) => onUpdate({ client: v })}
              disabled={!clientEnabled}
              disabledLabel={hasActivity ? 'Not applicable' : 'Pick activity first'}
              searchPlaceholder="Search clients…"
            />
          </div>

          <div
            style={{ gridColumn: 'span 4', opacity: epicEnabled ? 1 : 0.5 }}
          >
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Parent epic
              <span
                title="Auto-filled from the Jira ticket"
                style={{
                  marginLeft: 6,
                  color: 'var(--sp-fg-4)',
                  display: 'inline-flex',
                  verticalAlign: 'middle',
                }}
              >
                <Icon name="lock" size={10} />
              </span>
            </label>
            <div
              className="tt-input"
              style={{
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'var(--sp-bg-muted)',
                color: 'var(--sp-fg-2)',
              }}
            >
              {ticket.parentEpicId ? (
                <>
                  <span style={{ fontFamily: 'DM Sans', fontWeight: 700, color: '#0052CC', fontSize: 13 }}>
                    {ticket.parentEpicId}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontSize: 13 }}>
                    {ticket.parentEpicTitle}
                  </span>
                </>
              ) : (
                <span style={{ fontStyle: 'italic', color: 'var(--sp-fg-3)', fontSize: 13 }}>
                  Standalone ticket — no parent epic
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Header({ onClose, selectedDate }) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px 28px',
        borderBottom: '1px solid var(--sp-border)',
        background: '#fff',
        flex: 'none',
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: '#E9F2FF',
            color: '#0052CC',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="jiraMark" size={18} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--sp-fg-3)',
            }}
          >
            Import from Jira
          </span>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
            {fmtLong(selectedDate)}
          </span>
        </div>
      </div>
      <div style={{ flex: 1 }} />
      <button className="btn btn-ghost" onClick={onClose}>
        <Icon name="x" size={14} /> Close
      </button>
    </header>
  );
}

/**
 * Full-screen Jira Import takeover. Peer to the Calendar Import. Queries
 * /api/jira/activity for tickets the user touched on `selectedDate`
 * (worklog/comments/transitions, plus today's still-open assigned
 * tickets), lets them pick which to import, and creates each as a time
 * entry stamped `source = "jira:<ticketKey>:<date>"` so re-opens of the
 * picker grey them out instead of allowing duplicates.
 */
export function JiraImport({
  open,
  onClose,
  user,
  selectedDate,
  dictionary,
  onImported,
  // v2.4 session-expiry seams. `restoredDrafts` (present only on the post
  // re-auth boot) rehydrates the user's in-progress edits instead of starting
  // fresh; `onDraftsChange` lets the app shell observe the live draft so it can
  // snapshot it synchronously when the session lapses.
  restoredDrafts = null,
  onDraftsChange,
}) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [available, setAvailable] = useState(true);
  const scrollRef = useRef(null);

  const iso = useMemo(() => fmtISO(selectedDate), [selectedDate]);

  const freshDraft = (t) => ({
    included: true,
    description: t.ticketTitle || t.ticketId,
    activity: '',
    product: '',
    client: null,
    hours: 0,
    minutes: 0,
  });

  const seedDrafts = useCallback(
    (list) => {
      const init = {};
      list.forEach((t) => {
        if (t.alreadyImported) return;
        // Rehydrate after re-auth: keep the user's edits for tickets still
        // present on this day, falling back to a fresh draft for any new ones.
        init[t.ticketId] = restoredDrafts?.[t.ticketId] || freshDraft(t);
      });
      return init;
    },
    [restoredDrafts],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.jiraActivity(iso);
      setAvailable(data.available !== false);
      const list = data.tickets || [];
      setTickets(list);
      setDrafts(seedDrafts(list));
    } catch {
      setError('Could not load Jira tickets.');
    } finally {
      setLoading(false);
    }
  }, [iso, seedDrafts]);

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open, refresh]);

  // Surface the live draft to the app shell so a session lapse can snapshot it
  // (with the same ready/incomplete counts the footer shows) before redirect.
  useEffect(() => {
    if (!onDraftsChange) return;
    const meta = dictionary?.activityMeta || {};
    const dft =
      dictionary?.defaultFieldRules ||
      { product: 'required', client: 'hidden', epic: 'hidden' };
    const { importable: rdy, incomplete: inc } = classifyJiraRows({
      tickets,
      drafts,
      department: user.department,
      activityMeta: meta,
      defaults: dft,
    });
    onDraftsChange({
      drafts,
      ready: rdy.length,
      incomplete: inc.length,
      rows: rdy.length + inc.length,
    });
  }, [drafts, tickets, dictionary, user.department, onDraftsChange]);

  if (!open) return null;

  const updateDraft = (id, patch) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const activityMeta = dictionary?.activityMeta || {};
  const defaults = dictionary?.defaultFieldRules || {
    product: 'required',
    client: 'hidden',
    epic: 'hidden',
  };

  const { importable, incomplete } = classifyJiraRows({
    tickets,
    drafts,
    department: user.department,
    activityMeta,
    defaults,
  });

  const totalMins = importable.reduce((s, t) => {
    const d = drafts[t.ticketId];
    return (
      s +
      (parseInt(d.hours, 10) || 0) * 60 +
      (parseInt(d.minutes, 10) || 0)
    );
  }, 0);

  const jumpToFirstBlocker = () => {
    if (incomplete.length === 0 || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(
      `[data-ticket-id="${incomplete[0].ticketId}"]`,
    );
    if (!el) return;
    const top = el.offsetTop - 16;
    scrollRef.current.scrollTo({ top, behavior: 'smooth' });
    setTimeout(() => {
      const focusable = el.querySelector(
        'button[role="combobox"], button, input:not([disabled])',
      );
      if (focusable && typeof focusable.focus === 'function') focusable.focus();
    }, 320);
  };

  const handleConfirm = async () => {
    if (importable.length === 0 || importing) return;
    setImporting(true);
    setError(null);
    const rows = importable.map((ticket) => {
      const d = drafts[ticket.ticketId];
      const rules =
        activityMeta[`${user.department}::${d.activity}`]?.fieldRules ||
        defaults;
      return {
        ticketId: ticket.ticketId,
        payload: buildJiraImportEntry({
          ticket,
          draft: d,
          fieldRules: rules,
          isoDate: iso,
        }),
      };
    });
    const { created, createdIds, failed, busy, aborted } = await runJiraImport({
      rows,
      createEntry: api.createEntry,
    });
    setImporting(false);
    // Retire the rows that saved so a "try again for the rest" retry only
    // re-submits the failures — otherwise the succeeded tickets would be
    // imported a second time as duplicates.
    if (createdIds.length) {
      const saved = new Set(createdIds);
      setTickets((list) =>
        list.map((t) =>
          saved.has(t.ticketId) ? { ...t, alreadyImported: true } : t,
        ),
      );
      setDrafts((current) => {
        const next = { ...current };
        for (const id of saved) delete next[id];
        return next;
      });
    }
    // On `aborted` the session expired mid-batch — the global 401 handler
    // is already redirecting to login, so don't flash a row-level error.
    if (!aborted && failed.length) {
      setError(
        busy
          ? `Server busy — imported ${created} of ${importable.length}. Try again for the rest.`
          : `Imported ${created} of ${importable.length} — couldn't save: ${failed.join(', ')}.`,
      );
    }
    if (created > 0) onImported?.(created);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--sp-bg-page)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'tt-fade-in 200ms ease',
      }}
      data-screen-label="Import from Jira"
    >
      <Header onClose={onClose} selectedDate={selectedDate} />

      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}
      >
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          {error && (
            <div className="tt-error-msg" style={{ marginBottom: 12 }}>
              <Icon name="alert" size={12} /> {error}
            </div>
          )}

          {loading ? (
            <div
              className="card"
              style={{
                padding: 18,
                borderRadius: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div
                style={{
                  color: 'var(--sp-fg-3)',
                  fontSize: 13,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span className="tt-spinner sm" /> Looking up your Jira
                tickets…
              </div>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{ display: 'flex', gap: 12, alignItems: 'center' }}
                >
                  <div className="tt-skel" style={{ width: 80, height: 32 }} />
                  <div className="tt-skel" style={{ flex: 1, height: 18 }} />
                </div>
              ))}
            </div>
          ) : !available ? (
            <div
              className="card"
              style={{ padding: 40, borderRadius: 16, textAlign: 'center' }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: '#FFEAD5',
                  color: '#C4320A',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <Icon name="alert" size={22} />
              </div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                Jira is unavailable
              </h3>
              <p
                style={{
                  margin: '6px 0 0',
                  color: 'var(--sp-fg-3)',
                  fontSize: 14,
                }}
              >
                We couldn't reach the Jira API. Try again in a moment, or log
                time manually from the Today view.
              </p>
            </div>
          ) : tickets.length === 0 ? (
            <div
              className="card"
              style={{ padding: 40, borderRadius: 16, textAlign: 'center' }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: 'var(--sp-bg-muted)',
                  color: 'var(--sp-fg-3)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <Icon name="jiraMark" size={22} />
              </div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                No Jira tickets on this day
              </h3>
              <p
                style={{
                  margin: '6px 0 0',
                  color: 'var(--sp-fg-3)',
                  fontSize: 14,
                }}
              >
                No tickets were attributed to your account on this date.
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  marginBottom: 14,
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: '#E9F2FF',
                  color: '#0052CC',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Icon name="sparkle" size={14} />
                <span>
                  <strong>
                    {tickets.length} ticket{tickets.length === 1 ? '' : 's'}{' '}
                    found.
                  </strong>{' '}
                  Both the ticket and its parent epic (when present) are saved with each entry.
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {tickets.map((ticket) => (
                  <TicketRow
                    key={ticket.ticketId}
                    ticket={ticket}
                    draft={drafts[ticket.ticketId] || {}}
                    dept={user.department}
                    dictionary={dictionary}
                    onUpdate={(patch) => updateDraft(ticket.ticketId, patch)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <footer
        style={{
          padding: '14px 28px',
          borderTop: '1px solid var(--sp-border)',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flex: 'none',
        }}
      >
        <div style={{ flex: 1, fontSize: 13, color: 'var(--sp-fg-2)' }}>
          {importable.length === 0 && incomplete.length === 0 ? (
            <span style={{ color: 'var(--sp-fg-3)' }}>Nothing selected to import</span>
          ) : importable.length === 0 ? (
            <span style={{ color: 'var(--sp-apricot-ink)', fontWeight: 600 }}>
              {incomplete.length} ticket{incomplete.length === 1 ? '' : 's'} need an activity to import
            </span>
          ) : (
            <span>
              <strong>{importable.length}</strong> ready ·{' '}
              <strong>{fmtHM(totalMins)}</strong> total
              {incomplete.length > 0 && (
                <>
                  {' '}
                  · <strong style={{ color: 'var(--sp-apricot-ink)' }}>{incomplete.length}</strong> incomplete
                </>
              )}
            </span>
          )}
        </div>
        <button className="btn btn-ghost" onClick={onClose} disabled={importing}>
          Cancel
        </button>
        {importing ? (
          <button
            className="btn btn-primary"
            disabled
            style={{ whiteSpace: 'nowrap' }}
          >
            <span className="tt-spinner sm" style={{ marginRight: 6 }} />
            Importing…
          </button>
        ) : (
          <ImportSplitAction
            readyCount={importable.length}
            incompleteCount={incomplete.length}
            tone="jira"
            noun="entry"
            nounPlural="entries"
            onImport={handleConfirm}
            onImportAndSkip={handleConfirm}
            onJumpToBlocker={jumpToFirstBlocker}
          />
        )}
      </footer>
    </div>
  );
}
